import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, BarChart3, User, LogOut, Settings, Sliders, Award, ClipboardList, Menu, X } from 'lucide-react';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import DifficultySettingsModal from './DifficultySettingsModal';
import ModeSwitcher from './ModeSwitcher';
import SessionRecapModal from './SessionRecapModal';
import ThemeToggle from './ThemeToggle';
import { trackEvent } from '../utils/analytics';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const { isUnityMode, syncWithUser } = useLearningMode();

  // Le contexte de mode ne connaît l'utilisateur (userIdRef) qu'après syncWithUser(),
  // qui n'était appelé que par HomePage : un changement de mode fait depuis n'importe quelle
  // autre page (progression, exercice, certification…) n'était jamais persisté (PUT settings
  // sauté), et le prochain chargement de l'accueil re-synchronisait l'ancien mode depuis le
  // backend → l'étudiant « revenait » en Unity. Le header est monté sur toutes les pages
  // authentifiées : on synchronise ici (idempotent, syncedRef dans le contexte).
  useEffect(() => {
    if (user) syncWithUser(user.id);
  }, [user?.id, syncWithUser]);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isRecapModalOpen, setIsRecapModalOpen] = useState(false);
  // Sous lg (1024px) le sélecteur de mode, la nav et les trois actions (difficulté, session,
  // déconnexion) passent dans un panneau replié derrière un bouton « menu » : sur un téléphone
  // (390px) la rangée logo + sélecteur + grappe droite mesurait 689px et faisait défiler toute
  // la page horizontalement ; à 768px elle débordait encore (825px). Le panneau est dans le flux
  // du header (pas de position: fixed), donc pas concerné par le piège du backdrop-blur.
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
    navigate('/login');
  };

  const openSettings = () => {
    setIsMenuOpen(false);
    trackEvent('difficulty_settings_opened');
    setIsSettingsModalOpen(true);
  };

  const openRecap = () => {
    setIsMenuOpen(false);
    trackEvent('session_recap_opened');
    setIsRecapModalOpen(true);
  };

  const menuActions = [
    { label: 'Paramètres de difficulté', title: 'Paramètres de difficulté', icon: Sliders, onClick: openSettings },
    { label: 'Ma session : le bilan du jour', title: 'Terminer ma session : le bilan du jour', icon: ClipboardList, onClick: openRecap },
    { label: 'Se déconnecter', title: 'Se déconnecter', icon: LogOut, onClick: handleLogout },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/progression', label: 'Progression', icon: BarChart3 },
    // Certification only visible in Unity mode
    ...(isUnityMode ? [{ path: '/certification', label: 'Certification', icon: Award }] : []),
    // Admin access - only show for admin role
    ...(user?.role === 'admin' ? [
      { path: '/admin', label: 'Administration', icon: Settings }
    ] : [])
  ];

  return (
    <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-sm border-b border-gray-200 dark:border-slate-800 transition-colors sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and navigation — espacements compactés sous 1280px pour tenir à 1024px.
              À partir de xl les libellés de nav reviennent, et le header admin
              (pire cas) mesuré sans repli faisait 1305 px pour 1216 px utiles (max-w-7xl) :
              le flex écrasait le logo (« Unity / Learning » sur 2 lignes) et les boutons
              du sélecteur de mode (« Unity / C# »). Espacements xl resserrés (8/16/8 px
              au lieu de 24/32/16) → 1185 px, plus de retour à la ligne. */}
          <div className="flex items-center space-x-4 xl:space-x-4 min-w-0">
            <Link to="/" className="flex items-center space-x-2 group min-w-0">
              <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg ${
                isUnityMode
                  ? 'bg-gradient-to-br from-blue-500 to-purple-600'
                  : 'bg-gradient-to-br from-yellow-500 to-orange-500'
              }`}>
                <span className="text-white font-bold text-sm">{isUnityMode ? 'UL' : 'CL'}</span>
              </div>
              <span className="text-xl font-bold whitespace-nowrap truncate text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {isUnityMode ? 'Unity Learning' : 'Code Learning'}
              </span>
            </Link>

            <div className="hidden lg:block">
              <ModeSwitcher />
            </div>

            <nav className="hidden lg:flex items-center space-x-2 xl:space-x-2">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  title={label}
                  className={`flex items-center space-x-2 px-2 py-2 rounded-md text-sm font-medium transition-all duration-200 hover:scale-105 ${isActive(path)
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {/* Libellés masqués sous xl (1280) : le header tient dans max-w-7xl (~1216px utiles) sur les laptops 1024-1279 — cf. C1 */}
                  <span className="hidden xl:inline">{label}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* User menu */}
          <div className="flex items-center space-x-2 xl:space-x-2 shrink-0">
            <ThemeToggle />

            <Link to="/profile" title={`${user?.pseudonym ?? ''} — Voir le profil`} className="flex items-center space-x-3 cursor-pointer group">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center transition-transform group-hover:scale-110">
                <User className="w-4 h-4 text-white" />
              </div>
              {/* Nom masqué : le header ne peut pas afficher les libellés du cluster droit ET la nav dans max-w-7xl (cf. C1). Le pseudo reste dans le title + page profil. */}
              <div className="hidden">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{user?.pseudonym}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Voir le profil</p>
              </div>
            </Link>

            <button
              onClick={openSettings}
              className="hidden lg:flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              title="Paramètres de difficulté"
            >
              <Sliders className="w-4 h-4" />
              <span className="sr-only">Paramètres</span>
            </button>

            {/* B3 — Récap de fin de session : bouton discret, modale statique instantanée */}
            <button
              onClick={openRecap}
              className="hidden lg:flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              title="Terminer ma session : le bilan du jour"
            >
              <ClipboardList className="w-4 h-4" />
              <span className="sr-only">Ma session</span>
            </button>

            <button
              onClick={handleLogout}
              className="hidden lg:flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              title="Se déconnecter"
            >
              <LogOut className="w-4 h-4" />
              <span className="sr-only">Déconnexion</span>
            </button>

            <button
              type="button"
              onClick={() => setIsMenuOpen((open) => !open)}
              className="lg:hidden flex items-center p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              title={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMenuOpen}
              aria-controls="header-menu"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Menu replié (sous lg) : sélecteur de mode, navigation, actions */}
        {isMenuOpen && (
          <div id="header-menu" className="lg:hidden border-t border-gray-200 dark:border-slate-800 pt-3 pb-3 space-y-3">
            <div className="px-1">
              <ModeSwitcher />
            </div>

            <nav className="flex flex-col space-y-1">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(path)
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-300'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </Link>
              ))}
            </nav>

            <div className="flex flex-col space-y-1 border-t border-gray-200 dark:border-slate-800 pt-3">
              {menuActions.map(({ label, title, icon: Icon, onClick }) => (
                <button
                  key={label}
                  type="button"
                  onClick={onClick}
                  title={title}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-left text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <DifficultySettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />

      <SessionRecapModal
        isOpen={isRecapModalOpen}
        onClose={() => setIsRecapModalOpen(false)}
      />
    </header>
  );
};

export default Header;